/* eslint-disable filenames/match-exported */

import { ApolloClient } from '@apollo/client';
import * as Sentry from '@sentry/nextjs';
import _, { find, identity, min, pickBy } from 'lodash';
import pWaterfall from 'p-waterfall';
import React from 'react';
import wait from 'wait';
import DEFAULT_TEST_CENTER_ID from '~/constants/DEFAULT_TEST_CENTER_ID';
import DEVICE_CHECK_OS_MAP from '~/constants/DEVICE_CHECK_OS_MAP';
import DEVICE_CHECKS, { DEVICE_CHECKS_THRESHOLD } from '~/constants/DEVICE_CHECKS';
import NAMES from '~/constants/NAMES';
import OS_ENUM_MAP from '~/constants/OS_ENUM_MAP';
import AppContext, { AppContextType } from '~/context/AppContext';
import { ExamineeDeviceCheckResultsDocument, HydrateDeviceChecksToRunDocument, PostExamineeDeviceCheckResultsDocument, PostTestCenterDeviceCheckResultsDocument, StaffTestCenterDeviceCheckResultsDocument } from '~/generated/apollo';
import { BlacklistedApp, DeviceCheckResult, DeviceCheckStage, DeviceCheckType, HydrateDeviceChecksToRunQuery, OperatingSystem, PostExamineeDeviceCheckResultsMutation, PostTestCenterDeviceCheckResultsMutation, UnityInstall, UnityMinScreenSize, UnityPreCheckJit } from '~/generated/graphql';
import { enterElectronKioskMode, exitElectronKioskMode } from '~/helpers/electronKioskMode';
import { CheckStageRequirement, CheckStageResult, DeviceCheckContextStateType, DeviceCheckResults } from '~/types';
import DeviceCheckContext, { deviceCheckContextInitialState } from '.';
interface Props {
  children: React.ReactNode;
  client?: ApolloClient<unknown>;
}
interface IDeviceCheckContextValue {
  actions: {
    resetContext: (stateToKeep?: Partial<DeviceCheckContextStateType>) => Promise<void>;
    run: ({
      registrationId,
      shouldExitElectronKioskModeOnDone,
      testCenterId,
      type,
      registrationDetails,
      isBYOD
    }: {
      registrationId?: string;
      shouldExitElectronKioskModeOnDone?: boolean;
      testCenterId: string;
      type: DeviceCheckType;
      registrationDetails: string;
      isBYOD: boolean;
    }) => Promise<void>;
  };
  state: DeviceCheckContextStateType;
}
let isTCExempted: boolean = false;
export interface IChecksToRun {
  checksObj: UnityPreCheckJit | UnityInstall | undefined | null;
  externalApplicationsProhibited: boolean | undefined;
  registrationDetails: string | undefined;
  osKeyName: keyof UnityMinScreenSize;
  blacklistedApps: BlacklistedApp[];
}
export const getChecksToRun = ({
  checksObj,
  externalApplicationsProhibited,
  registrationDetails,
  osKeyName,
  blacklistedApps
}: IChecksToRun): CheckStageRequirement[] => {
  const checksToRun: CheckStageRequirement[] = [];
  const getCheckToRunValue = (checkToRun: {
    [key: string]: string | undefined | null;
  }): string | undefined => checkToRun?.[osKeyName] ?? undefined;
  if (checksObj && OS_ENUM_MAP[osKeyName === 'ChromeOS' ? 'Chrome OS' : osKeyName] !== OperatingSystem.Chromeos && externalApplicationsProhibited) {
    checksToRun.push({
      blacklistedApps,
      checkStage: DeviceCheckStage.ExternalApplicationsProhibited
    });
  }
  if (checksObj?.minimumUnityVersion) {
    checksToRun.push({
      checkStage: DeviceCheckStage.MinimumUnityVersion,
      expectedValue: getCheckToRunValue(checksObj?.minimumUnityVersion),
      registrationDetails
    });
  }
  if (checksObj?.minimumOSVersion) {
    checksToRun.push({
      checkStage: DeviceCheckStage.MinimumOsVersion,
      expectedValue: getCheckToRunValue(checksObj?.minimumOSVersion),
      registrationDetails
    });
  }
  if (checksObj?.requiresDeviceLockdown) {
    checksToRun.push({
      checkStage: DeviceCheckStage.RequiresDeviceLockdown,
      registrationDetails
    });
  }
  if (checksObj?.externalStorageProhibited) {
    checksToRun.push({
      checkStage: DeviceCheckStage.ExternalStorageProhibited,
      registrationDetails
    });
  }
  if (checksObj?.minScreenSize) {
    checksToRun.push({
      checkStage: DeviceCheckStage.MinScreenSize,
      expectedValue: getCheckToRunValue(checksObj?.minScreenSize),
      registrationDetails
    });
  }
  if (checksObj?.minimumBatteryLevel) {
    checksToRun.push({
      checkStage: DeviceCheckStage.MeasuredBatteryLevel,
      expectedValue: String(checksObj?.minimumBatteryLevel),
      registrationDetails
    });
  }
  if (checksObj?.multipleMonitorsProhibited) {
    checksToRun.push({
      checkStage: DeviceCheckStage.MultipleMonitorsProhibited,
      registrationDetails
    });
  }
  if (checksObj?.requiredACTEndpoint) {
    checksToRun.push({
      checkStage: DeviceCheckStage.RequiredActEndpoint,
      registrationDetails,
      urls: [checksObj?.requiredACTEndpoint]
    });
  }
  if (checksObj && 'requiredProductEndpoints' in checksObj) {
    checksToRun.push({
      checkStage: DeviceCheckStage.RequiredProductEndpoint,
      registrationDetails,
      urls: checksObj?.requiredProductEndpoints
    });
  }
  if (checksObj?.requiresDeviceIntegrity) {
    checksToRun.push({
      checkStage: DeviceCheckStage.RequiresDeviceIntegrity,
      registrationDetails
    });
  }
  if (checksObj?.requiresSecureNetwork) {
    checksToRun.push({
      checkStage: DeviceCheckStage.RequiresSecureNetwork,
      registrationDetails
    });
  }
  return checksToRun;
};
export const getResult = (checkResults: CheckStageResult[]): string => find(checkResults, {
  result: DeviceCheckResult.Fail
}) ? DeviceCheckResult.Fail : DeviceCheckResult.Pass;
export const getExpectedValue = (checkResult: CheckStageResult): string | undefined => checkResult.expectedValue ? String(checkResult.expectedValue) : undefined;
export const getValue = (checkResult: CheckStageResult): string | undefined => checkResult.value ? String(checkResult.value) : undefined;
class DeviceCheckContextProvider extends React.Component<Props, DeviceCheckContextStateType> {
  state: DeviceCheckContextStateType = deviceCheckContextInitialState;
  static contextType = AppContext;
  context!: AppContextType;
  setStateAsync = (state: Partial<DeviceCheckContextStateType>): Promise<void> => {
    // eslint-disable-next-line promise/avoid-new
    return new Promise((resolve): void => {
      this.setState(state as DeviceCheckContextStateType, resolve);
    });
  };
  _hydrateChecksToRun = async ({
    testCenterId,
    type,
    registrationDetails
  }: {
    testCenterId: string;
    type: DeviceCheckType;
    registrationDetails: string;
  }): Promise<{
    checksToRun: CheckStageRequirement[];
    configId: string;
    configVersion: string;
    programs: string[];
    displayOnDevice: boolean;
    productId: string;
    productName: string;
    productVersion?: string | null;
    blacklistedApps: BlacklistedApp[];
  }> => {
    const {
      client
    } = this.props;
    const {
      state: {
        operatingSystem
      }
    } = this.context;
    let configId = '';
    let configVersion = '';
    let programs = [''];
    let displayOnDevice = false;
    let productId = '';
    let productName = '';
    let productVersion;
    let checksToRun: CheckStageRequirement[] = [];
    let blacklistedApps: BlacklistedApp[] = [];
    if (client && operatingSystem) {
      const {
        data
      } = await client.query<HydrateDeviceChecksToRunQuery>({
        fetchPolicy: 'network-only',
        query: HydrateDeviceChecksToRunDocument,
        variables: {
          input: {
            testcenterid: testCenterId || DEFAULT_TEST_CENTER_ID
          }
        }
      });
      const calculatorEndpoint = data.getUnityConfig.product.attributes?.accessibility?.calculatorEndpoint ?? '';
      sessionStorage.setItem(NAMES.CALCULATOR_ENDPOINT, calculatorEndpoint);
      blacklistedApps = data.getUnityConfig.product.attributes?.gatewayAppConfig?.blacklistedApps ?? [];
      await this.setStateAsync({
        calculatorEndpoint
      });
      configId = data.getUnityConfig._id;
      configVersion = data.getUnityConfig.version;
      displayOnDevice = data.getUnityConfig.product.displayOnDevice;
      programs = data.getUnityConfig.product.attributes?.programs as string[];
      productId = data.getUnityConfig.product._id;
      productName = data.getUnityConfig.product.name;
      productVersion = data.getUnityConfig.product.version;
      let checksObj: UnityPreCheckJit | UnityInstall | undefined | null;
      let externalApplicationsProhibited: boolean | undefined;
      switch (type) {
        case DeviceCheckType.Install:
          checksObj = data.getUnityConfig.product.deviceCheck.install;
          externalApplicationsProhibited = checksObj?.externalApplicationsProhibited;
          break;
        case DeviceCheckType.Jit:
          checksObj = data.getUnityConfig.product.deviceCheck.JIT;
          externalApplicationsProhibited = checksObj?.externalApplicationsProhibited;
          break;
        case DeviceCheckType.Precheck:
          checksObj = data.getUnityConfig.product.deviceCheck.preCheck;
          externalApplicationsProhibited = checksObj?.externalApplicationsProhibited;
          break;
        default:
          break;
      }
      const osKeyName = DEVICE_CHECK_OS_MAP[operatingSystem];
      const exemptTCs: string[] = data.getUnityConfig.product.attributes?.gatewayAppConfig?.openedAppsCheckExemptions as string[] || [];
      isTCExempted = exemptTCs.includes(testCenterId);
      if (checksObj) {
        checksToRun = getChecksToRun({
          blacklistedApps,
          checksObj,
          externalApplicationsProhibited,
          osKeyName,
          registrationDetails
        });
        await this.setStateAsync({
          checksToRun
        });
      }
    }
    return {
      blacklistedApps,
      checksToRun,
      configId,
      configVersion,
      displayOnDevice,
      productId,
      productName,
      productVersion,
      programs
    };
  };
  _resetContext = async (stateToKeep?: Partial<DeviceCheckContextStateType>): Promise<void> => {
    await this.setStateAsync({
      ...deviceCheckContextInitialState,
      ...stateToKeep
    });
  };
  _captureCheckStageResult = async (checkStage: DeviceCheckStage, {
    expectedValue,
    result,
    value
  }: DeviceCheckResults): Promise<void> => {
    const {
      checksToRun,
      checkResults,
      currentIndex,
      percentProgress
    } = this.state;
    const newIndex = currentIndex + 1;
    await this.setStateAsync({
      checkResults: [...checkResults, {
        checkStage,
        expectedValue,
        result,
        value
      }],
      currentIndex: min([newIndex, checksToRun.length - 1]),
      percentProgress: Math.round(percentProgress + 1 / checksToRun.length * 100)
    });
  };
  _postDeviceCheckResults = async ({
    configId,
    configVersion,
    displayOnDevice,
    productId,
    productName,
    productVersion,
    type,
    registrationId,
    programs,
    testCenterId
  }: {
    configId: string;
    configVersion: string;
    displayOnDevice: boolean;
    productId: string;
    productName: string;
    productVersion?: string | null;
    type: DeviceCheckType;
    registrationId?: string;
    testCenterId: string;
    programs: string[];
  }): Promise<void> => {
    const {
      client
    } = this.props;
    const {
      checkResults
    } = this.state;
    const {
      state: {
        deviceId,
        operatingSystem
      }
    } = this.context;
    const osEnumValue = operatingSystem ? OS_ENUM_MAP[operatingSystem] : undefined;
    if (client && osEnumValue) {
      if (registrationId) {
        await client.mutate<PostExamineeDeviceCheckResultsMutation>({
          mutation: PostExamineeDeviceCheckResultsDocument,
          refetchQueries: [{
            fetchPolicy: 'network-only',
            query: ExamineeDeviceCheckResultsDocument,
            variables: {
              input: {
                deviceid: deviceId,
                includeCompleteResult: true,
                registrationid: registrationId
              }
            }
          }],
          variables: {
            input: {
              deviceid: deviceId,
              registrationid: registrationId,
              results: {
                date: new Date().toISOString(),
                deviceCheck: type,
                identifier: configId,
                operatingSystem: osEnumValue,
                product: {
                  attributes: {
                    programs
                  },
                  checkResults: {
                    result: getResult(checkResults),
                    tests: checkResults.map(checkResult => pickBy({
                      deviceCheckStage: checkResult.checkStage,
                      expectedValue: getExpectedValue(checkResult),
                      result: checkResult.result,
                      value: getValue(checkResult)
                    }, identity))
                  },
                  displayOnDevice,
                  identifier: productId,
                  isDisabled: false,
                  name: productName,
                  version: productVersion
                },
                version: configVersion
              }
            }
          }
        });
      } else {
        await client.mutate<PostTestCenterDeviceCheckResultsMutation>({
          mutation: PostTestCenterDeviceCheckResultsDocument,
          refetchQueries: [{
            fetchPolicy: 'network-only',
            query: StaffTestCenterDeviceCheckResultsDocument,
            variables: {
              input: {
                deviceid: deviceId,
                includeCompleteResult: true,
                tcid: testCenterId
              }
            }
          }],
          variables: {
            input: {
              deviceid: deviceId,
              results: {
                date: new Date().toISOString(),
                deviceCheck: type,
                identifier: configId,
                operatingSystem: osEnumValue,
                product: {
                  attributes: {
                    programs
                  },
                  checkResults: {
                    result: getResult(checkResults),
                    tests: checkResults.map(checkResult => pickBy({
                      deviceCheckStage: checkResult.checkStage,
                      expectedValue: getExpectedValue(checkResult),
                      result: checkResult.result,
                      value: getValue(checkResult)
                    }, identity))
                  },
                  displayOnDevice,
                  identifier: productId,
                  isDisabled: false,
                  name: productName,
                  version: productVersion
                },
                version: configVersion
              },
              testcenterid: testCenterId
            }
          }
        });
      }
    }
  };
  _run = async ({
    registrationId,
    shouldExitElectronKioskModeOnDone,
    testCenterId,
    type,
    registrationDetails,
    isBYOD
  }: {
    registrationId?: string;
    shouldExitElectronKioskModeOnDone?: boolean;
    testCenterId: string;
    type: DeviceCheckType;
    registrationDetails: string;
    isBYOD: boolean;
  }): Promise<void> => {
    const {
      state: {
        deviceId,
        user
      }
    } = this.context;
    try {
      await this._resetContext({
        isRunning: true
      });
      await enterElectronKioskMode(this.context);
      const {
        checksToRun,
        configId,
        configVersion,
        displayOnDevice,
        programs,
        productId,
        productName,
        productVersion,
        blacklistedApps
      } = await this._hydrateChecksToRun({
        registrationDetails,
        testCenterId,
        type
      });
      await wait(500);
      const promisesToExecute: (() => Promise<void>)[] = [];
      let filteredChecksToRun = _.cloneDeep(checksToRun);
      if (!isBYOD && isTCExempted) {
        filteredChecksToRun = checksToRun.filter(check => check.checkStage !== DeviceCheckStage.ExternalApplicationsProhibited);
      }
      filteredChecksToRun.forEach((checkToRun): void => {
        promisesToExecute.push(async (): Promise<void> => {
          const startTime = performance.now();
          const results = await DEVICE_CHECKS[checkToRun.checkStage].check({
            blacklistedApps,
            expectedValue: checkToRun.expectedValue,
            registrationDetails,
            urls: checkToRun.urls
          });
          const endTime = performance.now();
          const elapsedTime = parseFloat((endTime - startTime).toFixed(2));
          const isTresholdExceeded = DEVICE_CHECKS_THRESHOLD < elapsedTime;
          Sentry.captureMessage(`Device check - DeviceId: ${deviceId}, TC: ${testCenterId}, RegId: ${registrationId}; ${checkToRun.checkStage}, User: ${user} Took ${elapsedTime} ms to complete`, `${isTresholdExceeded ? 'warning' : 'info'}`);
          await wait(50);
          await this._captureCheckStageResult(checkToRun.checkStage, results);
        });
      });
      await pWaterfall(promisesToExecute);
      await this.setStateAsync({
        percentProgress: 100
      });
      await this._postDeviceCheckResults({
        configId,
        configVersion,
        displayOnDevice,
        productId,
        productName,
        productVersion,
        programs,
        registrationId,
        testCenterId,
        type
      });
      if (shouldExitElectronKioskModeOnDone) {
        await exitElectronKioskMode(this.context);
      }
      await this.setStateAsync({
        hasError: false
      });
      await this.setStateAsync({
        isRunning: false
      });
    } catch (err) {
      Sentry.captureException(err);
      await this.setStateAsync({
        hasError: true,
        isRunning: false
      });
    }
  };
  _getContextValue(): IDeviceCheckContextValue {
    return {
      actions: {
        resetContext: this._resetContext,
        run: this._run
      },
      state: {
        ...this.state
      }
    };
  }
  render(): React.ReactNode {
    const {
      children
    } = this.props;
    return <DeviceCheckContext.Provider value={this._getContextValue()} data-sentry-element="unknown" data-sentry-component="DeviceCheckContextProvider" data-sentry-source-file="provider.tsx">
        {children}
      </DeviceCheckContext.Provider>;
  }
}
export default DeviceCheckContextProvider;